import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { RefreshCw, Plus, Pencil, Trash2, X } from "lucide-react";

const AVAILABLE_THEMES = [
  { value: "corporate", label: "Corporate", description: "Logo-focused professional designs" },
  { value: "culture", label: "Culture", description: "Startup/company culture vibes" },
  { value: "artistic", label: "Artistic", description: "Creative, bold artistic designs" },
  { value: "thematic", label: "Thematic", description: "Theme-based (fitness, food, travel...)" },
  { value: "personality", label: "Personality", description: "Influencer/personal brand style" },
  { value: "lifestyle", label: "Lifestyle", description: "Lifestyle and everyday aesthetics" },
  { value: "mission", label: "Mission", description: "NGO/cause-driven messaging" },
  { value: "experience", label: "Experience", description: "Hospitality/experience focused" },
];

export default function SourceCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    slug: "",
    name: "",
    source_type: "website",
    description: "",
    keywords: "",
    store_themes: ["corporate"],
    display_order: 0,
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getSourceCategories();
      setCategories(data.data || []);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleInitialize = async () => {
    try {
      const result = await adminApi.initSourceCategories();
      toast.success(`Initialized ${result.total} categories`);
      fetchCategories();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const resetForm = () => {
    setForm({
      slug: "",
      name: "",
      source_type: "website",
      description: "",
      keywords: "",
      store_themes: ["corporate"],
      display_order: 0,
    });
  };

  const handleCreate = async () => {
    if (!form.slug || !form.name) {
      toast.error("Slug and name are required");
      return;
    }
    try {
      await adminApi.createSourceCategory({
        ...form,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      toast.success("Category created");
      setShowCreate(false);
      resetForm();
      fetchCategories();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleUpdate = async () => {
    if (!editingCategory) return;
    try {
      await adminApi.updateSourceCategory(editingCategory.id, {
        name: form.name,
        description: form.description,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        store_themes: form.store_themes,
        display_order: form.display_order,
        is_active: form.is_active,
      });
      toast.success("Category updated");
      setEditingCategory(null);
      resetForm();
      fetchCategories();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await adminApi.deleteSourceCategory(id);
      toast.success("Category deleted");
      fetchCategories();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openEdit = (cat) => {
    setEditingCategory(cat);
    setForm({
      slug: cat.slug,
      name: cat.name,
      source_type: cat.source_type,
      description: cat.description || "",
      keywords: (cat.keywords || []).join(", "),
      store_themes: cat.store_themes || ["corporate"],
      display_order: cat.display_order || 0,
      is_active: cat.is_active !== false,
    });
  };

  const toggleTheme = (theme) => {
    if (form.store_themes.includes(theme)) {
      setForm({ ...form, store_themes: form.store_themes.filter((t) => t !== theme) });
    } else {
      setForm({ ...form, store_themes: [...form.store_themes, theme] });
    }
  };

  const filteredCategories = filter === "all"
    ? categories
    : categories.filter((c) => c.source_type === filter);

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Source Categories</h1>
          <p className="text-gray-500">Manage detection categories and collection themes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleInitialize}>
            <RefreshCw className="w-4 h-4 mr-2" /> Initialize from Defaults
          </Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" /> Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>New Source Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Slug</Label>
                    <Input
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      placeholder="startup_tech"
                    />
                  </div>
                  <div>
                    <Label>Source Type</Label>
                    <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Startup/Tech"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="SaaS, fintech, tech company"
                  />
                </div>
                <div>
                  <Label>Keywords (comma-separated)</Label>
                  <Textarea
                    value={form.keywords}
                    onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                    placeholder="saas, platform, app, software, tech, api..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Collection Themes</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {AVAILABLE_THEMES.map((theme) => (
                      <Badge
                        key={theme.value}
                        variant={form.store_themes.includes(theme.value) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleTheme(theme.value)}
                      >
                        {theme.label}
                        {form.store_themes.includes(theme.value) && (
                          <X className="w-3 h-3 ml-1" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button onClick={handleCreate} className="w-full">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")}>
          All
        </Button>
        <Button variant={filter === "website" ? "default" : "outline"} size="sm" onClick={() => setFilter("website")}>
          Website
        </Button>
        <Button variant={filter === "instagram" ? "default" : "outline"} size="sm" onClick={() => setFilter("instagram")}>
          Instagram
        </Button>
      </div>

      {/* Categories Table */}
      <Card>
        <CardHeader>
          <CardTitle>Categories ({filteredCategories.length})</CardTitle>
          <CardDescription>
            Each category determines which collection themes are generated for detected sources
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Keywords</TableHead>
                <TableHead>Themes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCategories.map((cat) => (
                <TableRow key={cat.slug} className={!cat.is_active ? "opacity-50" : ""}>
                  <TableCell>
                    <div className="font-medium">{cat.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{cat.slug}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={cat.source_type === "website" ? "default" : "secondary"}>
                      {cat.source_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[200px] truncate text-xs text-gray-500">
                      {(cat.keywords || []).slice(0, 5).join(", ")}
                      {(cat.keywords || []).length > 5 && "..."}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(cat.store_themes || []).map((theme) => (
                        <Badge key={theme} variant="outline" className="text-xs">
                          {theme}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {cat.is_hardcoded ? (
                      <Badge variant="secondary">Hardcoded</Badge>
                    ) : (
                      <Badge variant={cat.is_active ? "default" : "destructive"}>
                        {cat.is_active ? "Active" : "Inactive"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {!cat.is_hardcoded && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => openEdit(cat)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(cat.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {cat.is_hardcoded && (
                      <span className="text-xs text-gray-400">Click "Initialize" to edit</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingCategory} onOpenChange={(open) => !open && setEditingCategory(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit: {editingCategory?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Keywords (comma-separated)</Label>
              <Textarea
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>Collection Themes</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {AVAILABLE_THEMES.map((theme) => (
                  <Badge
                    key={theme.value}
                    variant={form.store_themes.includes(theme.value) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleTheme(theme.value)}
                    title={theme.description}
                  >
                    {theme.label}
                    {form.store_themes.includes(theme.value) && <X className="w-3 h-3 ml-1" />}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {form.store_themes.length} theme(s) = {form.store_themes.length * 9} products per store
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label>Active</Label>
            </div>
            <Button onClick={handleUpdate} className="w-full">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
